import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mic, Video, Monitor, Radio, ArrowRight, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '../components/ui/dialog';
import SEOHead from '../components/SEOHead';

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = location.pathname.startsWith('/de') ? 'de' : 'en';
  const [selectedImage, setSelectedImage] = useState(null);

  const seoContent = {
    en: {
      title: 'Podcast Studio Zurich | Professional Video Podcast, Webinar & Live Stream Production',
      description: "Zurich's premier podcast production studio. Record professional video podcasts, audio podcasts, webinars, and live streams. Full-service production with editing, multi-camera setup, and content creation in the heart of Switzerland.",
    },
    de: {
      title: 'Podcast Studio Zürich | Professionelle Video Podcast, Webinar & Live Stream Produktion',
      description: 'Zürichs führendes Podcast-Produktionsstudio. Nehmen Sie professionelle Video-Podcasts, Audio-Podcasts, Webinare und Live-Streams auf. Full-Service-Produktion mit Editing, Multi-Kamera-Setup und Content-Erstellung im Herzen der Schweiz.',
    }
  };

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoContent[currentLang].title,
    "description": seoContent[currentLang].description,
    "url": `https://podcastzurich.com/${currentLang}`,
    "inLanguage": currentLang === 'de' ? 'de-CH' : 'en',
    "isPartOf": {
      "@type": "WebSite",
      "name": "Podcast Zurich",
      "url": "https://podcastzurich.com"
    },
    "about": {
      "@type": "LocalBusiness",
      "name": "Podcast Zurich",
      "serviceType": ["Podcast Studio", "Video Podcast Production", "Webinar Production", "Live Streaming"]
    }
  };

  const studioImages = [
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/re8fiacy_DSC05748.jpg', alt: 'Podcast Zurich Studio Interior' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/clpliiob_DSC05761.jpg', alt: 'Studio Equipment' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/fdatbmi6_DSC05765.jpg', alt: 'Recording Setup' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/hmxpzcsd_DSC05782.jpg', alt: 'Professional Microphones' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/trxd2xbp_DSC05785.jpg', alt: 'Studio Details' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/t4gm6h93_DSC05888-2.jpg', alt: 'Studio Atmosphere' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/kei6bwd4_DSC05912-2.jpg', alt: 'Recording Space' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/d7jz64rk_DSC05915-2.jpg', alt: 'Professional Studio Setup' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/qjc2qidi_DSC06174.jpg', alt: 'Studio Recording Area' },
    { url: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/5eymyo0h_DSC06186.jpg', alt: 'Studio Finishing Touches' },
  ];

  const content = {
    en: {
      hero: {
        title: 'Make an Impact',
        highlight: 'That Matters',
        subtitle: 'Your professional space in the heart of Zurich where meaningful conversations come to life. Share your expertise, connect with your audience, and create content that resonates.',
        cta: 'Explore Packages',
        secondary: 'Book a Call',
      },
      services: {
        title: 'What We Offer',
        subtitle: 'Everything you need to bring your vision to life',
        items: [
          {
            icon: 'mic',
            title: 'Audio Podcasts',
            description: 'Intimate conversations captured with exceptional sound quality in a comfortable setting',
          },
          {
            icon: 'video',
            title: 'Video Podcasts',
            description: 'Visual storytelling with professional production in our inspiring studio space',
          },
          {
            icon: 'monitor',
            title: 'Webinars',
            description: 'Connect with your audience through engaging online experiences and knowledge sharing',
          },
          {
            icon: 'radio',
            title: 'Live Streams',
            description: 'Broadcast your message in real-time, creating authentic connections with your community',
          },
        ],
      },
      valueProposition: {
        title: 'One Recording = 10 Pieces of Content',
        subtitle: 'Your message deserves to reach further. We transform every conversation into a complete content ecosystem.',
        items: [
          {
            title: 'Full episode (audio & video)',
            description: 'Complete, professionally produced episode ready to publish on all platforms. Your core content that drives engagement.'
          },
          {
            title: 'Social media clips & reels',
            description: 'Bite-sized highlights optimized for Instagram, TikTok, and LinkedIn. Reach new audiences where they spend their time.'
          },
          {
            title: 'Behind-the-scenes content',
            description: 'Authentic moments that build connection with your audience. Show the human side of your brand.'
          },
          {
            title: 'Professional cover images',
            description: 'Eye-catching thumbnails and artwork that make your content stand out. First impressions matter.'
          },
          {
            title: 'Guest spotlight moments',
            description: 'Shareable content featuring your guests that they\'ll want to promote. Expand your reach through their networks.'
          },
          {
            title: 'Shareable quotes & insights',
            description: 'Key takeaways formatted for maximum engagement. Perfect for daily social media posts that spark conversations.'
          },
          {
            title: 'Audiograms for platforms',
            description: 'Visual sound waves that capture attention on social media. Turn audio into scrollable, shareable content.'
          },
          {
            title: 'Episode highlights',
            description: 'Best moments compiled into preview clips. Help your audience discover the value before they commit to the full episode.'
          },
          {
            title: 'SEO-optimized transcript',
            description: 'Written version that helps people find you through search engines. Make your content discoverable and accessible.'
          },
          {
            title: 'Ready-to-share promotional clips',
            description: 'Pre-edited teasers designed to drive views and subscriptions. Launch each episode with momentum.'
          },
        ],
        benefit: 'Save weeks of content creation while maximizing your reach and impact across every channel.'
      },
      packages: {
        title: 'Choose Your Package',
        subtitle: 'Flexible options to match your needs and goals',
        items: [
          {
            name: 'Starter',
            description: 'Perfect for getting started with podcasting',
            price: 'CHF 5\'500',
            originalPrice: 'CHF 7\'500',
            features: [
              'Intro & Outro Recording and Editing',
              'Full episode editing',
              '1 hour editing review per episode included',
              '3 recording sessions',
              'Professional audio mastering',
              'Video recording included',
              '3 Social Media Clip per episode',
              'Picture of Host & Guest',
            ],
            ideal: 'Ideal for testing the waters'
          },
          {
            name: 'Professional',
            description: 'For serious content creators',
            price: 'Starting at CHF 12\'000',
            subPrice: 'for 6 full episodes and 60 pieces of content',
            features: [
              'Intro & Outro Recording and Editing',
              '6 recording sessions',
              'Professional audio mastering',
              'Multi-camera video setup',
              'Cameras Matching and Video Editing',
              'Full 10-piece content package',
              'Episode show notes',
              'Thumbnail designs',
            ],
            ideal: 'Most popular choice',
            popular: true
          },
          {
            name: 'Premium',
            description: 'Complete production service',
            price: 'Starting at CHF 30\'000',
            features: [
              'Launch Workshop with Producer and Brand Strategist',
              'Intro & Outro recording and editing for each episode',
              '12 recording sessions',
              'Professional audio mastering',
              'Cinematic video editing',
              'Full content ecosystem',
              'Social media strategy',
              'Monthly analytics report',
              'Dedicated producer support',
              'Publishing to your channels',
            ],
            ideal: 'For maximum impact'
          },
        ],
      },
      cta: {
        title: 'Ready to Start Your Journey?',
        subtitle: 'Join a community of thought leaders, innovators, and storytellers who trust Podcast Zurich',
        button: 'Let\'s Talk',
      },
      recording: {
        title: 'Recording Services',
        subtitle: 'Flexible hourly rates for your specific needs',
        video: {
          title: 'Video Recording',
          price: 'CHF 750',
          unit: '/hour',
          features: [
            'Multi-camera setup (up to 3 cameras)',
            'Professional lighting',
            'Raw video files delivery',
            'Cloud file transfer',
          ],
        },
        audio: {
          title: 'Audio Recording',
          price: 'CHF 250',
          unit: '/hour',
          features: [
            'Professional studio environment',
            'High-quality microphones',
            'Raw audio tracks delivery',
            'Cloud file transfer',
          ],
        },
        editing: {
          title: 'Editing Services',
          price: 'On Request',
          unit: '',
          features: [
            'Audio & video editing',
            '2D/3D animations',
            'Motion graphics',
            'Visual effects',
          ],
        },
      },
      pricingSummary: {
        title: 'Recording & Editing Services',
        subtitle: 'Flexible hourly rates for your specific needs',
        viewAll: 'View All Packages and Pricing Details',
        viewAllMobile: 'View Pricing',
        services: [
          { name: 'Audio Recording', price: 'CHF 250/hr' },
          { name: 'Video Recording', price: 'CHF 750/hr' },
          { name: 'Editing & Animations', price: 'On Request' },
        ],
      },
    },
    de: {
      hero: {
        title: 'Ihr Expertenwissen.',
        highlight: 'Professionell produziert.',
        subtitle: 'Wir schaffen den Raum, in dem Ihre Ideen natürlich zum Ausdruck kommen – professionell und entspannt.',
        cta: 'Pakete entdecken',
        secondary: 'Gespräch buchen',
      },
      services: {
        title: 'Was wir anbieten',
        subtitle: 'Alles, was Sie brauchen, um Ihre Vision zum Leben zu erwecken',
        items: [
          {
            icon: 'mic',
            title: 'Audio-Podcasts',
            description: 'Intime Gespräche mit außergewöhnlicher Klangqualität in komfortabler Umgebung',
          },
          {
            icon: 'video',
            title: 'Video-Podcasts',
            description: 'Visuelles Storytelling mit professioneller Produktion in unserem inspirierenden Studio',
          },
          {
            icon: 'monitor',
            title: 'Webinare',
            description: 'Verbinden Sie sich mit Ihrem Publikum durch ansprechende Online-Erlebnisse und Wissensaustausch',
          },
          {
            icon: 'radio',
            title: 'Live-Streams',
            description: 'Übertragen Sie Ihre Botschaft in Echtzeit und schaffen authentische Verbindungen',
          },
        ],
      },
      valueProposition: {
        title: 'Eine Aufnahme = 10 Content-Stücke',
        subtitle: 'Ihre Botschaft verdient es, weiter zu reichen. Wir verwandeln jedes Gespräch in ein komplettes Content-Ökosystem.',
        items: [
          {
            title: 'Vollständige Episode (Audio & Video)',
            description: 'Komplett professionell produzierte Episode bereit für alle Plattformen. Ihr Kerninhalt, der Engagement erzeugt.'
          },
          {
            title: 'Social-Media-Clips & Reels',
            description: 'Mundgerechte Highlights optimiert für Instagram, TikTok und LinkedIn. Erreichen Sie neue Zielgruppen dort, wo sie sich aufhalten.'
          },
          {
            title: 'Behind-the-Scenes-Content',
            description: 'Authentische Momente, die Verbindung zu Ihrem Publikum aufbauen. Zeigen Sie die menschliche Seite Ihrer Marke.'
          },
          {
            title: 'Professionelle Cover-Bilder',
            description: 'Auffällige Thumbnails und Artwork, die Ihren Content hervorheben. Der erste Eindruck zählt.'
          },
          {
            title: 'Gast-Spotlight-Momente',
            description: 'Teilbare Inhalte mit Ihren Gästen, die sie gerne weiterverbreiten. Erweitern Sie Ihre Reichweite durch deren Netzwerke.'
          },
          {
            title: 'Teilbare Zitate & Erkenntnisse',
            description: 'Kernaussagen formatiert für maximales Engagement. Perfekt für tägliche Social-Media-Posts, die Gespräche anregen.'
          },
          {
            title: 'Audiogramme für Plattformen',
            description: 'Visuelle Schallwellen, die auf Social Media Aufmerksamkeit erregen. Verwandeln Sie Audio in scrollbaren, teilbaren Content.'
          },
          {
            title: 'Episode-Highlights',
            description: 'Beste Momente in Preview-Clips zusammengestellt. Helfen Sie Ihrem Publikum, den Mehrwert zu entdecken.'
          },
          {
            title: 'SEO-optimiertes Transkript',
            description: 'Schriftliche Version, die Menschen über Suchmaschinen zu Ihnen führt. Machen Sie Ihren Content auffindbar und zugänglich.'
          },
          {
            title: 'Fertige Promotion-Clips',
            description: 'Vorproduzierte Teaser, die Views und Abos generieren. Starten Sie jede Episode mit Momentum.'
          },
        ],
        benefit: 'Sparen Sie Wochen an Content-Erstellung und maximieren Sie Ihre Reichweite und Wirkung über alle Kanäle.'
      },
      packages: {
        title: 'Wählen Sie Ihr Paket',
        subtitle: 'Flexible Optionen für Ihre Bedürfnisse und Ziele',
        items: [
          {
            name: 'Starter',
            description: 'Perfekt für den Einstieg ins Podcasting',
            price: 'CHF 5\'500',
            originalPrice: 'CHF 7\'500',
            features: [
              'Intro & Outro Aufnahme und Bearbeitung',
              'Vollständige Episode-Bearbeitung',
              '1 Stunde Editing-Review pro Episode inklusive',
              '3 Aufnahme-Sessions',
              'Professionelles Audio-Mastering',
              'Videoaufnahme inklusive',
              '3 Social-Media-Clips pro Episode',
              'Bild von Host & Gast',
            ],
            ideal: 'Ideal zum Ausprobieren'
          },
          {
            name: 'Professional',
            description: 'Für engagierte Content-Ersteller',
            price: 'Ab CHF 12\'000',
            subPrice: 'für 6 vollständige Episoden und 60 Content-Stücke',
            features: [
              'Intro & Outro Aufnahme und Bearbeitung',
              '6 Aufnahme-Sessions',
              'Professionelles Audio-Mastering',
              'Multi-Kamera Video-Setup',
              'Kamera-Matching und Video-Bearbeitung',
              'Vollständiges 10-Teile Content-Paket',
              'Episode Show Notes',
              'Thumbnail-Designs',
            ],
            ideal: 'Beliebteste Wahl',
            popular: true
          },
          {
            name: 'Premium',
            description: 'Kompletter Produktionsservice',
            price: 'Ab CHF 30\'000',
            features: [
              'Launch-Workshop mit Producer und Brand Strategist',
              'Intro & Outro Aufnahme und Bearbeitung für jede Episode',
              '12 Aufnahme-Sessions',
              'Professionelles Audio-Mastering',
              'Filmreife Video-Bearbeitung',
              'Vollständiges Content-Ökosystem',
              'Social-Media-Strategie',
              'Monatlicher Analytics-Report',
              'Dedizierter Producer-Support',
              'Veröffentlichung auf Ihren Kanälen',
            ],
            ideal: 'Für maximale Wirkung'
          },
        ],
      },
      cta: {
        title: 'Bereit, Ihre Reise zu beginnen?',
        subtitle: 'Werden Sie Teil einer Gemeinschaft von Vordenkern, Innovatoren und Geschichtenerzählern',
        button: 'Lassen Sie uns sprechen',
      },
      recording: {
        title: 'Aufnahme-Services',
        subtitle: 'Flexible Stundenpreise für Ihre spezifischen Bedürfnisse',
        video: {
          title: 'Video-Aufnahme',
          price: 'CHF 750',
          unit: '/Stunde',
          features: [
            'Multi-Kamera-Setup (bis zu 3 Kameras)',
            'Professionelle Beleuchtung',
            'Rohe Video-Dateien Lieferung',
            'Cloud-Dateitransfer',
          ],
        },
        audio: {
          title: 'Audio-Aufnahme',
          price: 'CHF 250',
          unit: '/Stunde',
          features: [
            'Professionelle Studio-Umgebung',
            'Hochwertige Mikrofone',
            'Rohe Audio-Tracks Lieferung',
            'Cloud-Dateitransfer',
          ],
        },
        editing: {
          title: 'Editing-Services',
          price: 'Auf Anfrage',
          unit: '',
          features: [
            'Audio- & Video-Editing',
            '2D/3D-Animationen',
            'Motion Graphics',
            'Visuelle Effekte',
          ],
        },
      },
      pricingSummary: {
        title: 'Aufnahme- & Editing-Services',
        subtitle: 'Flexible Stundenpreise für Ihre spezifischen Bedürfnisse',
        viewAll: 'Alle Pakete und Preisdetails anzeigen',
        viewAllMobile: 'Preise ansehen',
        services: [
          { name: 'Audio-Aufnahme', price: 'CHF 250/Std.' },
          { name: 'Video-Aufnahme', price: 'CHF 750/Std.' },
          { name: 'Editing & Animationen', price: 'Auf Anfrage' },
        ],
      },
    },
  };

  const t = content[currentLang];

  const getIcon = (iconName) => {
    const icons = {
      mic: Mic,
      video: Video,
      monitor: Monitor,
      radio: Radio,
    };
    const Icon = icons[iconName];
    return <Icon className="w-8 h-8" />;
  };

  return (
    <>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        canonicalPath={`/${currentLang}`}
        ogImage="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/re8fiacy_DSC05748.jpg"
        structuredData={homeStructuredData}
      />
      <main className="min-h-screen" role="main">
        {/* Hero Section with Video Background */}
        <section className="relative min-h-screen h-screen flex items-center overflow-hidden" aria-labelledby="hero-heading">
          {/* Video Background — true cover fit, no letterboxing on any device */}
          <div className="absolute inset-0 w-full h-full overflow-hidden">
            <iframe
              src="https://player.vimeo.com/video/1125200227?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1&background=1"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{
                pointerEvents: 'none',
                width: '100vw',
                height: '56.25vw', // 16:9 ratio based on width
                minHeight: '100vh',
                minWidth: '177.78vh', // 16:9 ratio based on height
              }}
              title="Podcast Zurich Studio Background"
            ></iframe>
          </div>
          
          {/* Dark Overlay at 10% */}
          <div className="absolute inset-0 bg-black/10"></div>
          
          {/* Content */}
          <div className="relative z-10 w-full px-6 py-32">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-2xl">
                <h1 id="hero-heading" className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
                  {t.hero.title}
                  <br />
                  <span style={{ color: '#ffff31', textShadow: '2px 2px 8px rgba(0, 0, 0, 0.5)' }}>
                    {t.hero.highlight}
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-white/90 mb-8 drop-shadow-md">
                  {t.hero.subtitle}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    size="lg"
                    style={{ backgroundColor: '#ffff31', color: '#000' }}
                    className="font-semibold hover:opacity-90 text-lg px-8"
                    onClick={() => {
                      navigate(`/${currentLang}/pricing`);
                      setTimeout(() => window.scrollTo(0, 0), 100);
                    }}
                    data-testid="explore-packages-btn"
                  >
                    {t.hero.cta}
                    <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="font-semibold text-lg px-8 bg-white/20 text-white border-white hover:bg-white hover:text-black"
                    onClick={() => {
                      window.open('https://koalendar.com/e/podcast-session', 'koalendar-popup', 'width=700,height=700,scrollbars=yes');
                    }}
                    data-testid="book-call-btn"
                  >
                    {t.hero.secondary}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-6 bg-white" aria-labelledby="services-heading">
          <div className="max-w-7xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="services-heading" className="text-4xl md:text-5xl font-bold mb-4">{t.services.title}</h2>
              <p className="text-xl text-gray-600">{t.services.subtitle}</p>
            </header>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" role="list">
              {t.services.items.map((service, index) => (
                <Card key={index} className="border-2 hover:border-gray-300 transition-all hover:shadow-lg" role="listitem">
                  <CardContent className="p-6">
                    <div className="mb-4" style={{ color: '#6b7280' }} aria-hidden="true">
                      {getIcon(service.icon)}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Studio Video Section */}
        <section id="studio-section" className="py-20 px-6 bg-white" aria-labelledby="studio-heading">
          <div className="max-w-5xl mx-auto">
            <header className="text-center mb-12">
              <h2 id="studio-heading" className="text-4xl md:text-5xl font-bold mb-4">
                {currentLang === 'en' ? 'Our Studio' : 'Unser Studio'}
              </h2>
              <p className="text-xl text-gray-600">
                {currentLang === 'en'
                  ? 'Located in the heart of Zurich at Werdstrasse 36'
                  : 'Im Herzen von Zürich an der Werdstrasse 36'}
              </p>
            </header>
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl mb-12">
              <iframe
                src="https://player.vimeo.com/video/1132614767?h=62c266d920&badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ width: '100%', height: '100%' }}
                title="Podcast Zurich Studio Tour - Professional podcast recording studio in Zurich Switzerland"
                loading="lazy"
              ></iframe>
            </div>
            
            {/* Studio Gallery */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-6xl mx-auto" role="list" aria-label="Studio gallery images">
              {studioImages.slice(0, 8).map((image, index) => (
                <figure 
                  key={index}
                  className="aspect-square overflow-hidden rounded-lg shadow-lg cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                  role="listitem"
                >
                  <img
                    src={image.url}
                    alt={`${image.alt} - Podcast Studio Zurich`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    width="400"
                    height="400"
                  />
                </figure>
              ))}
            </div>
            {/* Last 2 images centered */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 max-w-6xl mx-auto">
              <div className="hidden md:block"></div>
              {studioImages.slice(8, 10).map((image, index) => (
                <figure 
                  key={index + 8}
                  className="aspect-square overflow-hidden rounded-lg shadow-lg cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.url}
                    alt={`${image.alt} - Podcast Studio Zurich`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    width="400"
                    height="400"
                  />
                </figure>
              ))}
              <div className="hidden md:block"></div>
            </div>
          </div>
        </section>

      {/* Image Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-screen-xl w-full h-screen p-0 bg-black/95">
          <DialogClose className="absolute top-4 right-4 z-50 rounded-full bg-white/10 p-2 hover:bg-white/20 transition-colors">
            <X className="h-6 w-6 text-white" />
          </DialogClose>
          {selectedImage && (
            <div className="flex items-center justify-center w-full h-full p-8">
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-gray-900 text-white" aria-labelledby="cta-heading">
          <div className="max-w-4xl mx-auto text-center">
            <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold mb-6">{t.cta.title}</h2>
            <p className="text-xl text-gray-300 mb-8">{t.cta.subtitle}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                style={{ backgroundColor: '#ffff31', color: '#000' }}
                className="font-semibold hover:opacity-90 text-lg px-12"
                onClick={() => {
                  window.open('https://koalendar.com/e/podcast-session', 'koalendar-popup', 'width=700,height=700,scrollbars=yes');
                }}
                data-testid="book-call-cta-btn"
              >
                {currentLang === 'en' ? 'Book a Free Call' : 'Kostenloses Gespräch buchen'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="font-semibold text-lg px-12 text-white border-white hover:bg-white hover:text-black"
                onClick={() => {
                  navigate(`/${currentLang}/contact`);
                  setTimeout(() => window.scrollTo(0, 0), 100);
                }}
                data-testid="contact-us-btn"
              >
                {currentLang === 'en' ? 'Contact Us' : 'Kontaktieren Sie uns'}
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;